/**
 * Resolves sidebar menu items to absolute in-app routes.
 * API menu URLs may be missing, relative, or use legacy paths.
 */

export interface MenuRouteSource {
  url?: string | null;
  path?: string | null;
  menuName?: string | null;
  displayName?: string | null;
  name?: string | null;
}

/** Fallback routes when Url is missing (dashboards + role-specific labels). */
const MENU_NAME_ROUTES: Record<string, string> = {
  'value estimation': '/value-estimation',
  'sme dashboard': '/sme/dashboard',
  'assessment tracking': '/sme/assesstracking',
  'project reports': '/sme/project-reports',
  'superadmin dashboard': '/sa/dashboard',
  settings: '/sa/settings',
  'admin dashboard': '/admin/dashboard',
  'superuser dashboard': '/su/dashboard',
  dashboard: '/dashboard',
};

const normalizePath = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '#') {
    return '';
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/**
 * Returns an absolute app path for a menu item, or null when the item is not navigable
 * (e.g. parent folders like "Value Estimation Setup" with no URL).
 */
export const resolveMenuPath = (item: MenuRouteSource): string | null => {
  const rawUrl = item.url ?? item.path ?? '';
  const fromUrl = normalizePath(String(rawUrl));
  if (fromUrl) {
    return fromUrl;
  }

  const label = (item.menuName || item.displayName || item.name || '').trim();
  const labelKey = label.toLowerCase();
  if (MENU_NAME_ROUTES[labelKey]) {
    return MENU_NAME_ROUTES[labelKey];
  }

  return null;
};

/**
 * Role-aware menu path resolution. Admin users get their own dashboard route
 * while SuperAdmin and other roles are unchanged.
 */
export const resolveMenuPathForUser = (
  item: MenuRouteSource,
  roleKey?: string | null
): string | null => {
  const path = resolveMenuPath(item);
  if (!path) return null;

  if (roleKey === 'admin') {
    if (path === '/sa/dashboard') {
      return '/admin/dashboard';
    }

    const label = (item.menuName || item.displayName || item.name || '')
      .trim()
      .toLowerCase();
    if (label === 'superadmin dashboard' || label === 'admin dashboard') {
      return '/admin/dashboard';
    }
  }

  return path;
};

/** True when the menu node should appear in the sidebar (has view permission and a route). */
export const isNavigableMenuItem = (
  item: MenuRouteSource & { permissions?: Array<{ isAllowed?: number | boolean }> }
): boolean => {
  const hasPermission =
    Array.isArray(item.permissions) &&
    item.permissions.some((p) => Number(p.isAllowed) === 1);

  if (!hasPermission) {
    return false;
  }

  return resolveMenuPath(item) !== null;
};

const IMPORT_EXPORT_PATH = '/import-export';

const menuTreeHasPath = (items: MenuRouteSource[] | undefined, path: string): boolean => {
  if (!Array.isArray(items)) {
    return false;
  }

  return items.some((item) => {
    if (resolveMenuPath(item) === path) {
      return true;
    }
    return menuTreeHasPath((item as { subMenus?: MenuRouteSource[] }).subMenus, path);
  });
};

/**
 * Shows Import / Export in the client sidebar next to Assessment when the API
 * menu has not been seeded yet. No-ops if that route is already present.
 */
const SME_PROJECT_REPORTS_PATH = '/sme/project-reports';

/**
 * Adds Project Reports to the SME sidebar when Assessment Tracking is present
 * but the API menu has not been seeded yet.
 */
export const withSmeProjectReportsMenu = <T extends MenuRouteSource>(items: T[]): T[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return items;
  }

  if (menuTreeHasPath(items, SME_PROJECT_REPORTS_PATH)) {
    return items;
  }

  if (!menuTreeHasPath(items, '/sme/assesstracking')) {
    return items;
  }

  const reportItem = {
    id: 'sme-project-reports',
    menuName: 'Project Reports',
    displayName: 'Project Reports',
    url: SME_PROJECT_REPORTS_PATH,
    iconUrl: 'ReportIcon.svg',
    displayOrder: 25,
    permissions: [{ isAllowed: 1 }],
    subMenus: [],
  } as T;

  const trackingIndex = items.findIndex(
    (item) => resolveMenuPath(item) === '/sme/assesstracking'
  );
  if (trackingIndex < 0) {
    return [...items, reportItem];
  }

  const next = [...items];
  next.splice(trackingIndex + 1, 0, reportItem);
  return next;
};

export const withClientImportExportMenu = <T extends MenuRouteSource>(items: T[]): T[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return items;
  }

  if (menuTreeHasPath(items, IMPORT_EXPORT_PATH)) {
    return items;
  }

  if (!menuTreeHasPath(items, '/assessment')) {
    return items;
  }

  const importExportItem = {
    id: 'client-import-export',
    menuName: 'Import / Export',
    displayName: 'Import / Export',
    url: IMPORT_EXPORT_PATH,
    iconUrl: 'AssessmentIcon.svg',
    displayOrder: 15,
    permissions: [{ isAllowed: 1 }],
    subMenus: [],
  } as T;

  const assessmentIndex = items.findIndex((item) => resolveMenuPath(item) === '/assessment');
  if (assessmentIndex < 0) {
    return [...items, importExportItem];
  }

  const next = [...items];
  next.splice(assessmentIndex + 1, 0, importExportItem);
  return next;
};

export const SIDEBAR_SETTINGS_PATH = '/sa/settings';

export const isSidebarSettingsMenuItem = (
  item: MenuRouteSource,
  roleKey?: string | null
): boolean => {
  const path = resolveMenuPathForUser(item, roleKey);
  if (path === SIDEBAR_SETTINGS_PATH) {
    return true;
  }

  const name = (item.menuName || item.displayName || item.name || '').trim().toLowerCase();
  return name === 'settings';
};

/** Pull Settings out of the scrollable nav; render it in the sidebar footer instead. */
export const partitionSidebarMenuItems = <T extends MenuRouteSource>(
  items: T[],
  roleKey?: string | null
): { mainMenuItems: T[]; settingsMenuItem: T | null } => {
  if (!Array.isArray(items) || items.length === 0) {
    return { mainMenuItems: [], settingsMenuItem: null };
  }

  let settingsMenuItem: T | null = null;
  const mainMenuItems: T[] = [];

  for (const item of items) {
    if (isSidebarSettingsMenuItem(item, roleKey)) {
      settingsMenuItem = item;
    } else {
      mainMenuItems.push(item);
    }
  }

  return { mainMenuItems, settingsMenuItem };
};
